import React from 'react';
import { motion } from 'framer-motion';
import { FaClock, FaShieldAlt, FaExclamationTriangle, FaTrophy, FaUserSlash, FaChartLine } from 'react-icons/fa';
import { GiCrossedSwords, GiTwoCoins } from 'react-icons/gi';
import { MdBlock } from 'react-icons/md';

function Stage4() {
  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <motion.div 
          className="text-center mb-3"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: "spring",
            stiffness: 260,
            damping: 20,
            duration: 0.8 
          }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-3"
            style={{
              backgroundColor: "#374151",
              boxShadow: "0 4px 20px rgba(55, 65, 81, 0.5)",
            }}
          >
            <GiCrossedSwords className="text-white text-3xl" />
          </div>
          <motion.h1 
            className="text-3xl font-bold text-white mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            مرحله چهارم: دوئل نمایندگان
          </motion.h1>
        </motion.div>

        {/* Representatives Selection */}
        <motion.div 
          className="p-5 rounded-2xl backdrop-blur-xl border"
          style={{
            backgroundColor: "rgba(31, 41, 55, 0.6)",
            borderColor: "rgba(253, 13, 14, 0.3)",
          }}
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "#374151" }}
            >
              <GiCrossedSwords className="text-white text-lg" />
            </div>
            <h2 className="text-white font-bold text-lg">معرفی نمایندگان تیم‌ها</h2>
          </div>

          <p className="text-gray-300 text-sm text-right mb-3 leading-relaxed">
            هر تیم یک نماینده معرفی می‌کند. نمایندگان به صورت <span className="text-red-400 font-bold">دو به دو</span> با هم دوئل می‌کنند.
          </p>
        </motion.div>

        {/* Duel Rules */}
        <motion.div 
          className="p-5 rounded-2xl backdrop-blur-xl border"
          style={{
            backgroundColor: "rgba(31, 41, 55, 0.6)",
            borderColor: "rgba(253, 13, 14, 0.3)",
          }}
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "#374151" }}
            >
              <GiTwoCoins className="text-white text-lg" />
            </div>
            <h2 className="text-white font-bold text-lg">قوانین دوئل</h2>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center p-3 rounded-xl" style={{ backgroundColor: "rgba(55, 65, 81, 0.3)" }}>
              <div className="text-amber-400 text-base font-bold">3 معامله</div>
              <div className="text-gray-400 text-sm">تعداد</div>
            </div>
            <div className="text-center p-3 rounded-xl" style={{ backgroundColor: "rgba(55, 65, 81, 0.3)" }}>
              <div className="text-blue-400 text-base font-bold">دقیقه 1</div>
              <div className="text-gray-400 text-sm">تایم فریم</div>
            </div>
            <div className="text-center p-3 rounded-xl" style={{ backgroundColor: "rgba(55, 65, 81, 0.3)" }}>
              <div className="text-purple-400 text-base font-bold">5 دقیقه</div>
              <div className="text-gray-400 text-sm">حداکثر زمان</div>
            </div>
          </div>

          <p className="text-gray-300 text-sm text-right mb-3 leading-relaxed">
            با باز شدن هر معامله توسط یک نفر، <span className="text-red-400 font-bold">معامله معکوس</span> در حساب حریف باز خواهد شد.
          </p>

          {/* محدودیت پوزیشن همزمان */}
          <div className="p-3 rounded-xl mb-3 flex items-start gap-2" 
            style={{ 
              backgroundColor: "rgba(239, 68, 68, 0.1)", 
              borderRight: "3px solid #ef4444" 
            }}
          >
            <MdBlock className="text-red-400 text-lg mt-0.5 flex-shrink-0" />
            <p className="text-red-400 text-sm font-bold">
              در هر زمان فقط یک معامله می‌تواند باز باشد. امکان باز کردن پوزیشن همزمان وجود ندارد
            </p>
          </div>

          <div className="p-3 rounded-xl" style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", borderRight: "3px solid #10b981" }}>
            <p className="text-emerald-400 text-sm font-bold">برنده: نفری که در پایان معاملات سود بیشتری کرده باشد</p>
          </div>
        </motion.div>

        {/* Team Challenge */}
        <motion.div 
          className="p-5 rounded-2xl backdrop-blur-xl border"
          style={{
            backgroundColor: "rgba(31, 41, 55, 0.6)",
            borderColor: "rgba(253, 13, 14, 0.3)",
          }}
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "#374151" }}
            >
              <FaExclamationTriangle className="text-white text-lg" />
            </div>
            <h2 className="text-white font-bold text-lg">چالش درون تیمی</h2>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="text-center p-3 rounded-xl" style={{ backgroundColor: "rgba(55, 65, 81, 0.3)" }}>
              <div className="text-amber-400 text-base font-bold">30 دقیقه</div>
              <div className="text-gray-400 text-sm">زمان معامله</div>
            </div>
            <div className="text-center p-3 rounded-xl" style={{ backgroundColor: "rgba(55, 65, 81, 0.3)" }}>
              <div className="text-red-400 text-base font-bold">20%</div>
              <div className="text-gray-400 text-sm">درصد ریسک</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Stage4;